/* eslint-disable import/no-named-as-default-member */
import { once } from "lodash";
import ReactGA from "react-ga4";
import { makeUrlLowerCase } from "./navigationUtils";

function areCookiesEnabled() {
    try {
        document.cookie = "testcookie=test; Max-Age=10; SameSite=Strict;";
        const isEnabled = document.cookie.indexOf("testcookie") !== -1;
        document.cookie = "testcookie=; Max-Age=-1; SameSite=Strict;";
        return isEnabled;
    } catch {
        return false;
    }
}

function getCurrentCleanedUrl(): URL {
    const url = new URL(window.location.href);
    url.searchParams.delete("config_string");
    url.searchParams.delete("encoded_config");
    url.hash = "";
    makeUrlLowerCase(url);
    return url;
}

// The custom dimensions are not set globally in GA4, so any properties
// that are set here will be added to all events.
const CustomDimensions: any = {};

const GaReporter = {
    enabled: false,

    initTracking(trackingIds: string[], useCookies: boolean, cookiePath: string, appType: string, appVersion: string) {
        if (!areCookiesEnabled()) {
            useCookies = false;
        }

        if (!useCookies) {
            // TODO: Decide if we should show consent banner. Setting analytics_storage to denied disables cookies,
            // but also makes no data show up at all in GA, so now we set temporary session cookies even without consent.
            // ReactGA.gtag("consent", "default", { ad_storage: "denied", analytics_storage: "denied" });
        }

        GaReporter.setCustomDimension("tracking_type", useCookies ? "cookie" : "none");
        GaReporter.setCustomDimension("application_type", appType);
        GaReporter.setCustomDimension("application_version", appVersion);

        const url = getCurrentCleanedUrl();
        const options: any[] = trackingIds.map(trackingId => ({
            trackingId: trackingId,
            gtagOptions: useCookies
                ? {
                      cookie_domain: "auto",
                      cookie_path: cookiePath,
                      cookie_flags: "secure;samesite=none",
                      send_page_view: false,
                      page_location: url.href,
                  }
                : {
                      // TODO: Disable cookies entirely while still collecting statistics.
                      cookie_domain: "auto",
                      cookie_path: cookiePath,
                      cookie_flags: "secure;samesite=none",
                      cookie_expires: 1, // Store a temporary cookie that is removed after 1 sec.
                      send_page_view: false,
                      page_location: url.href,
                  },
        }));

        ReactGA.initialize(options);
        GaReporter.enabled = true;
        GaReporter.setReferrerUrl();
    },

    setReferrerUrl() {
        const searchParams = new URLSearchParams(window.location.search);
        const siteUrl = searchParams.get("site_url");
        // Prefer setting site_url as referrer when going back and
        // forth between model_selector and configurator.
        if (siteUrl) {
            const url = new URL(siteUrl);
            makeUrlLowerCase(url);
            ReactGA.gtag("set", "page_referrer", url.href);
        } else if (document.referrer) {
            const referrer = new URL(document.referrer);
            makeUrlLowerCase(referrer);
            ReactGA.gtag("set", "page_referrer", referrer.href);
        }
    },

    setCustomDimension(key: string, value: string) {
        CustomDimensions[key] = value;
    },

    setProject(project: string) {
        GaReporter.setCustomDimension("application_name", project);
    },

    setModelId(modelId: string) {
        GaReporter.setCustomDimension("model", modelId);
    },

    setAccountId(accountId: string) {
        GaReporter.setCustomDimension("account_id", accountId);
    },

    event(action: string, category?: string, label?: string, fields?: any) {
        ReactGA.event(action, {
            event_category: category,
            event_label: label,
            ...fields,
            ...CustomDimensions,
        });
    },

    trackPageView() {
        const url = getCurrentCleanedUrl();
        ReactGA.gtag("set", "page_location", url.href);
        GaReporter.event("page_view", undefined, undefined, { page_path: url.pathname });
    },

    /**
     * Creates a function to use as hitCallback for Google Analytics,
     * with a timeout that triggers the callback anyway in case the
     * analytics call fails or if GaReporter isn't enabled.
     */
    createHitCallback(callback: () => void) {
        const fn = once(callback);
        setTimeout(fn, GaReporter.enabled ? 1000 : 0);
        return fn;
    },
};

export default GaReporter;
